import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getSegmentsApi,
  previewSegmentApi,
  deleteSegmentApi,
  exportAudienceMatchApi,
} from '../api/audience.api';
import { apiHandler, unwrapData } from '../utils/apiHandler';
import { usePagination } from '../hooks/usePagination';
import Pagination from '../components/Pagination';
import PageHeader from '../components/PageHeader';
import {
  SEGMENT_TYPE_FILTERS,
  segmentTypeLabel,
  segmentTypeClass,
  parseFilterDefinition,
} from '../utils/audienceBuilderFilters';

function TrendBadge({ up, trend }) {
  if (up === true) return <span className="ss-trend-up"><i className="fas fa-arrow-trend-up" />{trend}</span>;
  if (up === false) return <span className="ss-trend-down"><i className="fas fa-arrow-trend-down" />{trend}</span>;
  return <span className="ss-trend-neutral">{trend}</span>;
}

export default function SavedSegments() {
  const navigate = useNavigate();
  const [apiSegments, setApiSegments] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [preview, setPreview] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const {
    page,
    limit,
    loading,
    setLoading,
    applyPaginationMeta,
    paginationProps,
  } = usePagination(20);

  function showToast(msg, type = 'info') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  }

  async function loadSegments(pageNum = page) {
    setLoading(true);
    const params = { page: pageNum, limit, type: typeFilter };

    const result = await apiHandler(() => getSegmentsApi(params));
    if (result.success) {
      const data = unwrapData(result);
      const rows = (data?.segments || []).map((s) => {
        const creator = s.creator;
        const creatorName = creator
          ? `${creator.firstName || ''} ${creator.lastName || ''}`.trim() || 'Admin'
          : 'Admin';
        const typeLbl = segmentTypeLabel(s.segmentType);
        return {
          key: s.id,
          name: s.segmentName,
          sub: s.description || 'Saved segment',
          users: s.estimatedUserCount || 0,
          lastUsed: s.lastRunAt
            ? new Date(s.lastRunAt).toLocaleDateString()
            : s.updatedAt
              ? new Date(s.updatedAt).toLocaleDateString()
              : '—',
          type: typeLbl,
          typeCls: segmentTypeClass(s.segmentType),
          segmentType: s.segmentType,
          filterDefinition: s.filterDefinition,
          raw: s,
          detail: {
            desc: s.description || s.segmentName,
            createdBy: creatorName,
            lastUsed: s.lastRunAt
              ? new Date(s.lastRunAt).toLocaleString()
              : s.updatedAt
                ? new Date(s.updatedAt).toLocaleString()
                : '—',
            source: 'Saved query',
            channel: s.defaultChannel
              ? String(s.defaultChannel).charAt(0).toUpperCase() + String(s.defaultChannel).slice(1)
              : 'Push + Email',
          },
        };
      });
      setApiSegments(rows);
      applyPaginationMeta(data);
      if (rows.length && !rows.find((r) => r.key === selectedKey)) {
        setSelectedKey(rows[0].key);
      }
      if (!rows.length) setSelectedKey(null);
    } else {
      showToast(result.message || 'Failed to load segments', 'error');
    }
    setLoading(false);
  }

  async function loadPreview(segmentId) {
    if (!segmentId) {
      setPreview(null);
      return;
    }
    setPreviewLoading(true);
    const result = await apiHandler(() => previewSegmentApi(segmentId));
    if (result.success) {
      setPreview(unwrapData(result));
    } else {
      setPreview(null);
    }
    setPreviewLoading(false);
  }

  useEffect(() => {
    loadSegments(page);
  }, [page, typeFilter]);

  useEffect(() => {
    loadPreview(selectedKey);
  }, [selectedKey]);

  async function handleDelete() {
    if (!selectedKey) return;
    if (!window.confirm('Delete this segment? This cannot be undone.')) return;
    const result = await apiHandler(() => deleteSegmentApi(selectedKey));
    if (result.success) {
      showToast('Segment deleted', 'success');
      setSelectedKey(null);
      setPreview(null);
      loadSegments(page);
    } else {
      showToast(result.message || 'Delete failed', 'error');
    }
  }

  async function handleExport() {
    const seg = apiSegments.find((s) => s.key === selectedKey);
    if (!seg?.filterDefinition) {
      showToast('No segment selected', 'error');
      return;
    }
    const filterDefinition = parseFilterDefinition(seg.filterDefinition);
    const result = await apiHandler(() =>
      exportAudienceMatchApi({ filterDefinition })
    );
    if (result.success && result.data) {
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${seg.name.replace(/\s+/g, '_')}-export.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('Export downloaded', 'success');
    } else {
      showToast(result.message || 'Export failed', 'error');
    }
  }

  function handleEditRules() {
    const seg = apiSegments.find((s) => s.key === selectedKey);
    if (!seg) return;
    navigate('/audience-builder', {
      state: {
        segmentId: seg.key,
        segmentName: seg.name,
        description: seg.raw?.description || seg.sub,
        segmentType: seg.segmentType,
        filterDefinition: parseFilterDefinition(seg.filterDefinition),
      },
    });
  }

  const selected = apiSegments.find((s) => s.key === selectedKey);
  const d = selected?.detail;

  const metrics = preview
    ? [
        {
          label: 'Estimated users',
          icon: 'fa-users',
          color: '#074f78',
          val: String(preview.estimatedCount ?? selected?.users ?? 0),
          trend: '—',
          up: null,
        },
        {
          label: 'Actual match',
          icon: 'fa-bullseye',
          color: '#15803D',
          val: String(preview.actualCount ?? 0),
          trend: preview.difference >= 0 ? `+${preview.difference}` : String(preview.difference),
          up: preview.difference >= 0,
        },
        {
          label: 'Accuracy',
          icon: 'fa-chart-line',
          color: '#B45309',
          val: `${preview.accuracy ?? 0}%`,
          trend: '—',
          up: null,
        },
      ]
    : (d?.metrics || []);

  return (
    <>
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 70,
            right: 20,
            zIndex: 9999,
            background: toast.type === 'success' ? '#F0FDF4' : '#EFF6FF',
            border: `1px solid ${toast.type === 'success' ? '#b7e5bb' : '#BFDBFE'}`,
            color: toast.type === 'success' ? '#15803D' : '#1D4ED8',
            padding: '10px 18px',
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,.08)',
          }}
        >
          <i
            className={`fas ${toast.type === 'success' ? 'fa-circle-check' : 'fa-circle-info'}`}
            style={{ marginRight: 7 }}
          />
          {toast.msg}
        </div>
      )}

      <PageHeader
        title="Saved Segments"
        icon="fa-bookmark"
        trail={[{ label: 'Saved Segments' }]}
        actions={
          <button
            type="button"
            className="action-btn btn-blue ss-new-btn"
            onClick={() => navigate('/audience-builder')}
          >
            <i className="fas fa-plus" />
            New Segment
          </button>
        }
      />

      <div className="ss-two-panel fade-up">
        <div className="section-card ss-library-card">
          <div className="section-header">
            <div className="section-title">
              <i className="fas fa-list-check" style={{ color: '#ec7426' }} />
              Segment library
            </div>
            <div className="ss-filter-pills">
              {SEGMENT_TYPE_FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  className={`ss-filter-pill${typeFilter === f.value ? ' active' : ''}`}
                  onClick={() => setTypeFilter(f.value)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="ss-lib-table-wrap">
            <table className="data-table ss-lib-table">
              <thead>
                <tr>
                  <th>Segment</th>
                  <th>Users</th>
                  <th>Last used</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4}>Loading…</td>
                  </tr>
                ) : apiSegments.length === 0 ? (
                  <tr>
                    <td colSpan={4}>No segments yet — create one in Audience Builder</td>
                  </tr>
                ) : (
                  apiSegments.map((seg) => (
                    <tr
                      key={seg.key}
                      className={`ss-seg-row${selectedKey === seg.key ? ' selected' : ''}`}
                      onClick={() => setSelectedKey(seg.key)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div className="ss-seg-name">{seg.name}</div>
                        <div className="ss-seg-sub">{seg.sub}</div>
                      </td>
                      <td>
                        <span className="ss-users-count">{seg.users.toLocaleString()}</span>
                      </td>
                      <td>
                        <span className="ss-last-used">{seg.lastUsed}</span>
                      </td>
                      <td>
                        <span className={`ss-type-badge ${seg.typeCls}`}>{seg.type}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination {...paginationProps} />
        </div>

        <div className="section-card ss-detail-card">
          <div className="section-header">
            <div className="section-title">
              <i className="fas fa-circle-info" style={{ color: '#074f78' }} />
              Segment detail
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" className="ss-icon-btn" title="Export" onClick={handleExport}>
                <i className="fas fa-arrow-up-from-bracket" />
              </button>
              <button
                type="button"
                className="ss-icon-btn"
                title="Refresh"
                onClick={() => {
                  loadSegments(page);
                  loadPreview(selectedKey);
                }}
              >
                <i className={`fas fa-rotate-right${previewLoading ? ' fa-spin' : ''}`} />
              </button>
              {selected && (
                <button type="button" className="ss-icon-btn" title="Delete" onClick={handleDelete}>
                  <i className="fas fa-trash" style={{ color: '#B91C1C' }} />
                </button>
              )}
            </div>
          </div>

          <div className="ss-detail-body">
            {!selected ? (
              <p style={{ color: '#64748B' }}>Select a segment</p>
            ) : previewLoading ? (
              <p style={{ color: '#64748B' }}>Loading preview…</p>
            ) : (
              <>
                <div className="ss-detail-top">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 12,
                      flexWrap: 'wrap',
                    }}
                  >
                    <div>
                      <h2 className="ss-detail-name">{selected.name}</h2>
                      <div className="ss-detail-desc-label">Description</div>
                      <p className="ss-detail-desc">{d?.desc}</p>
                    </div>
                    <span className={`ss-type-badge ${selected.typeCls}`}>{selected.type}</span>
                  </div>
                </div>

                {preview?.logicExpression && (
                  <div style={{ marginBottom: 16 }}>
                    <div className="ss-detail-desc-label">Rule logic</div>
                    <p
                      style={{
                        fontSize: 12,
                        fontFamily: 'monospace',
                        background: '#F8FAFC',
                        padding: 10,
                        borderRadius: 8,
                        border: '1px solid #E2E8F0',
                      }}
                    >
                      {preview.logicExpression}
                    </p>
                  </div>
                )}

                <div className="ss-metrics-section">
                  <div className="ss-metrics-header">
                    <span>Metric breakdown</span>
                    <span className="ss-metrics-updated">Live preview</span>
                  </div>
                  <table className="data-table ss-metrics-table">
                    <thead>
                      <tr>
                        <th>Metric</th>
                        <th>Value</th>
                        <th>Trend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metrics.map((m, i) => (
                        <tr key={i}>
                          <td>
                            <span className="ss-metric-label">
                              <i className={`fas ${m.icon}`} style={{ color: m.color }} />
                              {m.label}
                            </span>
                          </td>
                          <td>
                            <strong>{m.val}</strong>
                          </td>
                          <td>
                            <TrendBadge up={m.up} trend={m.trend} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {preview?.sampleUsers?.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div className="ss-detail-desc-label">Sample users</div>
                    <table className="data-table" style={{ fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>State</th>
                          <th>Plan</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.sampleUsers.slice(0, 5).map((u) => (
                          <tr key={u.id}>
                            <td>
                              {`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}
                            </td>
                            <td>{u.state || '—'}</td>
                            <td>{u.planType || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="ss-meta-grid">
                  {[
                    { icon: 'fa-user-pen', label: 'Created by', val: d?.createdBy, id: 'createdBy' },
                    { icon: 'fa-clock-rotate-left', label: 'Last used', val: d?.lastUsed, id: 'lastUsed' },
                    { icon: 'fa-database', label: 'Audience source', val: d?.source, id: 'source' },
                    { icon: 'fa-paper-plane', label: 'Default channel', val: d?.channel, id: 'channel' },
                  ].map((f) => (
                    <div key={f.id} className="ss-meta-field">
                      <div className="ss-meta-label">
                        <i className={`fas ${f.icon}`} /> {f.label}
                      </div>
                      <div className="ss-meta-value">{f.val}</div>
                    </div>
                  ))}
                </div>

                <div className="ss-detail-footer">
                  <button type="button" className="action-btn btn-blue ss-action-btn" onClick={handleEditRules}>
                    <i className="fas fa-sliders" />
                    Edit Rules
                  </button>
                  <button
                    type="button"
                    className="action-btn btn-green ss-action-btn"
                    onClick={() =>
                      navigate('/message-composer', {
                        state: { filterDefinition: selected.filterDefinition },
                      })
                    }
                  >
                    <i className="fas fa-paper-plane" />
                    Send Message
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
